import React from "react";
import { Card, Row, Col, Tag, List, Button, Empty, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;

// Label above value, stacked — avoids squeezing the value into whatever's
// left of a fixed label column on narrow screens. Full width on mobile,
// side-by-side on wider screens for short fields (span left at 24 for
// longer ones like address/email so they don't wrap awkwardly in a half column).
const Field = ({ label, value, span = 12 }) => (
  <Col xs={24} sm={span}>
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, color: "#262626", wordBreak: "break-word" }}>{value || "-"}</div>
    </div>
  </Col>
);

const STATUS_COLORS = {
  active: "green",
  inactive: "default",
  terminated: "red",
  probation: "orange",
};

const formatDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "-");

// Every document field the employee record can carry a scan/upload path for.
// Only the ones actually present for this employee get shown.
const DOCUMENT_DEFINITIONS = [
  { key: "passport_scan_path", label: "Passport", no: "passport_no", expiry: "passport_expiry" },
  { key: "visa_scan_path", label: "Visa", no: "visa_no", expiry: "visa_expiry" },
  { key: "e_id_scan_path", label: "National / Emirates ID", no: "national_id_no", expiry: "national_id_expiry" },
  { key: "labour_card_scan_path", label: "Labour Card", no: "labour_card_id", expiry: "labour_card_expiry" },
  { key: "labour_contract_scan_path", label: "Labour Contract", no: "labour_contract_id", expiry: "labour_contract_expiry" },
  { key: "labour_contract_id_scan_path", label: "Labour Contract (ID)", no: "labour_contract_id", expiry: "labour_contract_expiry" },
  { key: "insurance_scan_path", label: "Insurance", no: "insurance_name", expiry: "insurance_expiry" },
  { key: "offer_signed_file_path", label: "Offer Letter" },
  { key: "experience_letter_path", label: "Experience Letter" },
  { key: "warning_letter_path", label: "Warning Letter" },
  { key: "memo_document_path", label: "Memo" },
  { key: "signed_final_settlement_copy_path", label: "Final Settlement" },
  { key: "other_agreements_document_path", label: "Other Agreement" },
  { key: "visa_cancellation_copy_path", label: "Visa Cancellation Copy" },
  { key: "labour_cancellation_copy_path", label: "Labour Cancellation Copy" },
  { key: "insurance_cancellation_copy_path", label: "Insurance Cancellation Copy" },
  { key: "insurance_cancelled_refund_path", label: "Insurance Cancellation Refund" },
  { key: "client_labour_contract_scan_path", label: "Client Labour Contract" },
];

export default function EmployeeProfilePanel({ employee }) {
  if (!employee?.employee_id) return null;

  const documents = DOCUMENT_DEFINITIONS.filter((d) => employee[d.key]).map((d) => ({
    ...d,
    path: employee[d.key],
    noValue: d.no ? employee[d.no] : null,
    expiryValue: d.expiry ? employee[d.expiry] : null,
  }));

  const mobile = employee.contact_no
    ? `${employee.country_code ? "+" + employee.country_code + " " : ""}${employee.contact_no}`
    : "-";

  const emergency = employee.emergency_contact_no
    ? `${employee.emergency_country_code ? "+" + employee.emergency_country_code + " " : ""}${employee.emergency_contact_no}`
    : "-";

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
      <Col xs={24} lg={10}>
        <Card className="employee-profile-card">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 20,
            }}
          >
            <Text type="secondary">Employee ID: {employee.employee_code}</Text>
            <div>
              <Tag
                color={STATUS_COLORS[employee.employee_status] || "blue"}
                style={{ textTransform: "capitalize" }}
              >
                {employee.employee_status || "-"}
              </Tag>
              {employee.job_grade && <Tag>{employee.job_grade}</Tag>}
            </div>
          </div>

          <Row gutter={[16, 0]}>
            <Field label="Father Name" value={employee.father_name} />
            <Field label="Mother Name" value={employee.mother_name} />
            <Field label="Date of Birth" value={formatDate(employee.birth_date)} />
            <Field label="Gender" value={employee.gender_type} />
            <Field label="Marital Status" value={employee.marital_type} />
            <Field label="Joining Date" value={formatDate(employee.joining_date)} />
            <Field label="Work Location" value={employee.work_location} />
            <Field label="Mobile No." value={mobile} />
            <Field label="Email" value={employee.email_address} span={24} />
            <Field
              label="Emergency Contact"
              span={24}
              value={
                employee.emergency_name
                  ? `${employee.emergency_name} (${employee.emergency_relation || "-"}) - ${emergency}`
                  : null
              }
            />
            <Field label="Permanent Address" value={employee.permanent_address} span={24} />
            <Field label="Present Address" value={employee.present_address} span={24} />
          </Row>
        </Card>
      </Col>

      <Col xs={24} lg={14}>
        <Card title="Documents" className="employee-documents-card">
          {documents.length ? (
            <List
              itemLayout="horizontal"
              dataSource={documents}
              renderItem={(doc) => (
                <List.Item
                  actions={[
                    <Button
                      key="download"
                      size="small"
                      icon={<DownloadOutlined />}
                      href={doc.path}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={doc.label}
                    description={
                      [doc.noValue, doc.expiryValue ? `Expiry: ${formatDate(doc.expiryValue)}` : null]
                        .filter(Boolean)
                        .join(" | ") || undefined
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty description="No documents available" />
          )}
        </Card>
      </Col>
    </Row>
  );
}
